import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId } from '../utils/orgUtils'

function withOrganizationScope(url: string): string {
  const orgId = getActiveOrgId()
  if (!orgId) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}organization_id=${encodeURIComponent(orgId)}`
}

function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
}

async function parseJson(res: Response) {
  const body = await res.json()
  if (!res.ok) throw new Error(body?.message || body?.error || 'Request failed')
  return body?.data ?? body
}

export type EvidenceReuseCandidate = {
  id: string
  sourceStandardCode: string
  targetStandardCode: string
  reuseConfidence: number
  reuseReason?: string
  scopeCompatibility: string
  status: 'SUGGESTED' | 'CONFIRMED' | 'REJECTED'
  explanationPayload?: {
    explanation?: { summary?: string; lines?: Array<{ key: string; fallback: string }> }
    scope?: { status: string }
  }
  sourceEvidence?: { id: string; title: string; freshnessStatus?: string }
  canonicalControl?: { id: string; code: string }
  informationSystem?: { id: string; name: string }
}

export async function fetchReuseOpportunities(targetStandardCode?: string) {
  const q = targetStandardCode ? `?targetStandardCode=${encodeURIComponent(targetStandardCode)}` : ''
  const res = await fetch(withOrganizationScope(`${API_ENDPOINTS.COMPLIANCE_REUSE_OPPORTUNITIES}${q}`), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as {
    opportunities: EvidenceReuseCandidate[]
    sharedControls: Array<{ controlCode: string; standards: string[] }>
    remediationImpacts: Array<{ title: string; affectedStandards: string[]; explanation?: { summary?: string } }>
    graphOverlay?: { nodes: unknown[]; edges: unknown[] }
  }
}

export async function fetchSharedControls() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_REUSE_SHARED_CONTROLS), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as { sharedControls: Array<{ controlCode: string; standards: string[] }> }
}

export async function fetchSharedEvidence(targetStandardCode?: string) {
  const q = targetStandardCode ? `?targetStandardCode=${encodeURIComponent(targetStandardCode)}` : ''
  const res = await fetch(withOrganizationScope(`${API_ENDPOINTS.COMPLIANCE_REUSE_SHARED_EVIDENCE}${q}`), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as { sharedEvidence: { confirmed: EvidenceReuseCandidate[]; suggested: EvidenceReuseCandidate[] } }
}

export async function fetchSharedRemediation() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_REUSE_SHARED_REMEDIATION), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as {
    remediationImpacts: Array<{ title: string; affectedStandards: string[]; explanation?: { summary?: string } }>
    graphOverlay?: { nodes: unknown[]; edges: unknown[] }
  }
}

export async function fetchStandardReuseImpact(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_REUSE_IMPACT(standardCode)), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function confirmReuseCandidate(candidateId: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_REUSE_CONFIRM(candidateId)), {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function rejectReuseCandidate(candidateId: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_REUSE_REJECT(candidateId)), {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}
